/**
 * Onboarding: the "How to play" rules panel and the first-run coach-mark tutorial. [P6-help]
 *
 * Built because the interactions all work but are undiscoverable — see `.planning/lane-howto.md`. Rules
 * content is written against `.planning/research/rules-canon.md` §4–7, not from memory: goal, turn
 * structure, the four claimable features and their point values, the "unoccupied after merge" meeple
 * rule, majority ties, game end, and this build's off-by-default draw-stacks house rule.
 */

const TUTORIAL_DISMISSED_KEY = 'carc.tutorial.dismissed.v1';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

const FOCUSABLE = 'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * A scrollable, focus-trapped rules reference. One instance lives for the app's lifetime and is opened
 * from both the lobby and the in-game HUD, so its content is independent of any running session.
 */
export class HelpPanel {
  readonly element: HTMLElement;
  private readonly panel: HTMLElement;
  private previouslyFocused: HTMLElement | null = null;
  private open_ = false;

  constructor(private readonly onReplayTutorial: () => void) {
    this.element = el('div', 'help-overlay hidden');
    this.panel = el('div', 'help-panel panel');
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    this.panel.setAttribute('aria-labelledby', 'help-panel-title');
    this.element.appendChild(this.panel);
    document.body.appendChild(this.element);

    this.element.addEventListener('mousedown', (e) => {
      if (e.target === this.element) this.close();
    });
    document.addEventListener('keydown', this.handleKeydown);

    this.render();
  }

  get isOpen(): boolean {
    return this.open_;
  }

  open(): void {
    if (this.open_) return;
    this.open_ = true;
    this.previouslyFocused = document.activeElement as HTMLElement | null;
    this.element.classList.remove('hidden');
    const closeBtn = this.panel.querySelector<HTMLElement>('.help-close');
    closeBtn?.focus();
  }

  close(): void {
    if (!this.open_) return;
    this.open_ = false;
    this.element.classList.add('hidden');
    this.previouslyFocused?.focus();
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    if (!this.open_) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = [...this.panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (node) => node.offsetParent !== null,
    );
    if (focusable.length === 0) return;
    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  private render(): void {
    this.panel.innerHTML = '';

    const header = el('div', 'help-header');
    const title = el('h2', undefined, 'How to play');
    title.id = 'help-panel-title';
    const closeBtn = el('button', 'help-close', '✕');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', () => this.close());
    header.append(title, closeBtn);

    const body = el('div', 'help-body');
    body.append(
      section('The goal', [p('Most points wins. Points come from finishing roads, cities, cloisters, and — at the very end only — farms.')]),
      section('A turn', [
        ol([
          'Draw a tile.',
          "Place it so every edge matches its neighbours — field to field, road to road, city to city.",
          'Optionally put one of your 7 meeples on a feature of the tile you just placed, or skip.',
          'Anything that just finished scores immediately, and those meeples come home to your supply.',
        ]),
      ]),
      section('The four things you can claim', [
        table(
          ['Feature', 'Worth', 'Finishes when'],
          [
            ['Road (a thief)', '1 pt / tile', 'Both ends hit a junction, a city gate, a cloister, or the road loops back on itself.'],
            ['City (a knight)', '2 pts / tile + 2 / pennant closed · 1 / tile + 1 / pennant if still open at game end', 'No gaps in the wall.'],
            ['Cloister (a monk)', '9 pts finished · 1 per filled neighbour + 1 for itself if not', 'All 8 surrounding squares hold a tile.'],
            ['Farm (a farmer, lying down)', '3 pts for each finished city it touches, scored only at game end', 'Never — the farmer never comes back.'],
          ],
        ),
        p('A city or road pays full rate only while it is still open and gets closed by your move; left unfinished at game end, both pay half. A farmer costs you a meeple for the rest of the game.'),
      ]),
      section('Claiming a feature', [
        p('You can only place a meeple on a feature nobody already occupies — and that is judged after your tile is placed and merged with its neighbours, so a tile that joins your road to someone else\'s makes it unavailable to everyone.'),
      ]),
      section('Ties', [p('Everyone tied for the most meeples on a feature scores the full value — having more meeples than a tie does not multiply it.')]),
      section('Game end', [p('When the last tile is played, everything still unfinished scores at its reduced rate, then farms score.')]),
      section("This build's house rule", [
        p('The randomly-chosen first player may split the deck into 2–6 draw stacks and choose which to draw from each turn. Off by default — this is not a standard Carcassonne rule.'),
      ]),
      // Written down because a control nobody can find is a control that does not exist. Panning in
      // particular was always available to the camera and always unreachable in practice, since the
      // gestures that would have reached it were being taken by tile rotation. [P6-camera]
      section('Moving the board', [
        table(
          ['What', 'Mouse / trackpad', 'Touch'],
          [
            ['Turn the tile', 'R, the Rotate button, or a right-click', 'The Rotate button, or a two-finger tap'],
            ['Place the tile', 'Click a highlighted square', 'Tap a highlighted square'],
            ['Orbit the board', 'Drag with the left button on empty space', 'Drag with one finger'],
            ['Move the whole board', 'Drag with the right or middle button, or use the arrow keys', 'Drag with two fingers'],
            ['Zoom', 'Scroll wheel', 'Pinch with two fingers'],
            ['Jump somewhere', 'Click the mini-map', 'Tap the mini-map'],
          ],
        ),
        p('A right-click or two-finger tap turns the tile only if you keep still — drag instead and you move the board, so the two never fight.'),
      ]),
      section('Playing against the computer', [
        p('Open “Play online” and set any seat to AI (easy, steady or sharp). One human and two AI opponents is a perfectly good game, and nobody else has to join. Online is also how you play someone on another device — share the room code.'),
      ]),
      section('The turn timer', [
        p('Optional, and per turn rather than per game: choose it in the lobby before starting. When a turn runs out the tile is placed at the first legal spot and no meeple is taken — a meeple is never chosen for you.'),
      ]),
    );

    const footer = el('div', 'help-footer');
    const replayBtn = el('button', 'ghost', 'Replay tutorial');
    replayBtn.type = 'button';
    replayBtn.addEventListener('click', () => this.onReplayTutorial());
    footer.appendChild(replayBtn);

    this.panel.append(header, body, footer);
  }
}

function section(heading: string, children: HTMLElement[]): HTMLElement {
  const wrap = el('section', 'help-section');
  wrap.append(el('h3', undefined, heading), ...children);
  return wrap;
}

function p(text: string): HTMLElement {
  return el('p', undefined, text);
}

function ol(items: string[]): HTMLElement {
  const list = el('ol');
  for (const item of items) list.appendChild(el('li', undefined, item));
  return list;
}

function table(headings: string[], rows: string[][]): HTMLElement {
  const wrap = el('div', 'help-table-wrap');
  const t = el('table', 'help-table');
  const thead = el('thead');
  const headRow = el('tr');
  for (const h of headings) headRow.appendChild(el('th', undefined, h));
  thead.appendChild(headRow);
  const tbody = el('tbody');
  for (const row of rows) {
    const tr = el('tr');
    for (const cell of row) tr.appendChild(el('td', undefined, cell));
    tbody.appendChild(tr);
  }
  t.append(thead, tbody);
  wrap.appendChild(t);
  return wrap;
}

// ── First-run tutorial ────────────────────────────────────────────────────────────────────────────

export interface PlacementTutorialAnchors {
  readonly tilePreview: HTMLElement;
  readonly rotateButton: HTMLElement;
  readonly board: HTMLElement;
}

export interface MeepleTutorialAnchors {
  readonly skipButton: HTMLElement;
}

type Advance = 'next' | 'wait' | 'finish';

interface Step {
  readonly text: string;
  readonly cta: string;
  readonly advance: Advance;
}

const PLACEMENT_STEPS: readonly Step[] = [
  {
    text: 'This is your tile. Move the pointer over the board — the highlighted squares are where it can legally go.',
    cta: 'Next',
    advance: 'next',
  },
  { text: 'Not fitting? Rotate it.', cta: 'Next', advance: 'next' },
  { text: 'Click a highlighted square to place it.', cta: 'Got it', advance: 'wait' },
];

const MEEPLE_STEP: Step = {
  text: 'Now you may put a meeple on it — or skip. Meeples score you points, but you only have 7.',
  cta: 'Got it',
  advance: 'finish',
};

/**
 * 3–4 dismissible coach marks anchored to the real UI, shown once on this device (gated by
 * `localStorage`) for the first turn of the first game. "Skip tutorial" and finishing the last step are
 * both treated as "don't show again" — there is no second first turn to come back to.
 */
export class Tutorial {
  private readonly layer: HTMLElement;
  private card: HTMLElement | null = null;
  private stepIndex = 0;
  private anchor: HTMLElement | null = null;

  constructor() {
    this.layer = el('div', 'tutorial-layer');
    document.body.appendChild(this.layer);
    window.addEventListener('resize', () => this.reposition());
  }

  get dismissed(): boolean {
    try {
      return localStorage.getItem(TUTORIAL_DISMISSED_KEY) === '1';
    } catch {
      return true; // storage unavailable (private browsing etc.) — never force it on
    }
  }

  /** Clears the "seen" flag. Used by the How-to-play panel's "Replay tutorial". */
  reset(): void {
    try {
      localStorage.removeItem(TUTORIAL_DISMISSED_KEY);
    } catch {
      /* ignore */
    }
  }

  private dismiss(): void {
    try {
      localStorage.setItem(TUTORIAL_DISMISSED_KEY, '1');
    } catch {
      /* ignore */
    }
    this.hide();
  }

  hide(): void {
    this.anchor = null;
    if (this.card) {
      this.card.remove();
      this.card = null;
    }
  }

  enterPlacementPhase(anchors: PlacementTutorialAnchors): void {
    if (this.dismissed) return;
    this.stepIndex = 0;
    this.showPlacementStep(anchors);
  }

  enterMeeplePhase(anchors: MeepleTutorialAnchors): void {
    if (this.dismissed) return;
    this.stepIndex = PLACEMENT_STEPS.length;
    this.renderCard(MEEPLE_STEP, anchors.skipButton);
  }

  private showPlacementStep(anchors: PlacementTutorialAnchors): void {
    const step = PLACEMENT_STEPS[this.stepIndex];
    if (!step) return;
    const anchorEl = this.stepIndex === 1 ? anchors.rotateButton : this.stepIndex === 0 ? anchors.tilePreview : anchors.board;
    this.renderCard(step, anchorEl, anchors);
  }

  private renderCard(step: Step, anchorEl: HTMLElement, placementAnchors?: PlacementTutorialAnchors): void {
    if (this.card) this.card.remove();
    this.anchor = anchorEl;

    const card = el('div', 'coach-mark');
    card.setAttribute('role', 'note');
    const counter = el('div', 'coach-mark-counter', `Step ${this.stepIndex + 1} of ${PLACEMENT_STEPS.length + 1}`);
    const text = el('p', undefined, step.text);
    const actions = el('div', 'coach-mark-actions');

    const skipBtn = el('button', 'ghost', 'Skip tutorial');
    skipBtn.type = 'button';
    skipBtn.addEventListener('click', () => this.dismiss());

    const ctaBtn = el('button', 'primary', step.cta);
    ctaBtn.type = 'button';
    ctaBtn.addEventListener('click', () => {
      if (step.advance === 'next' && placementAnchors) {
        this.stepIndex += 1;
        this.showPlacementStep(placementAnchors);
      } else if (step.advance === 'wait') {
        this.hide();
      } else {
        this.dismiss();
      }
    });

    actions.append(skipBtn, ctaBtn);
    card.append(counter, text, actions);
    this.layer.appendChild(card);
    this.card = card;
    this.reposition();
    ctaBtn.focus();
  }

  private reposition(): void {
    if (!this.card || !this.anchor) return;
    const anchorRect = this.anchor.getBoundingClientRect();
    // Avoid the anchor's whole containing HUD panel, not just the button itself — the button usually
    // sits at the bottom edge of a taller panel (instruction text above it), and clearing only the
    // button left the card overlapping that text.
    const panel = this.anchor.closest('.panel') as HTMLElement | null;
    const avoidRect = panel ? panel.getBoundingClientRect() : anchorRect;
    const cardRect = this.card.getBoundingClientRect();
    const margin = 12;
    // The board-anchored step (a fullscreen canvas) would otherwise clamp to the very top of the
    // viewport, sliding the card under the HUD topbar.
    const topbar = document.querySelector('.hud-topbar') as HTMLElement | null;
    const minTop = (topbar?.getBoundingClientRect().bottom ?? 0) + margin;
    let top = avoidRect.bottom + margin;
    if (top + cardRect.height > window.innerHeight - margin) {
      top = avoidRect.top - cardRect.height - margin;
    }
    top = Math.max(minTop, Math.min(top, window.innerHeight - cardRect.height - margin));
    let left = anchorRect.left + anchorRect.width / 2 - cardRect.width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - cardRect.width - margin));
    this.card.style.top = `${top}px`;
    this.card.style.left = `${left}px`;
  }
}
